/* ============================================================
   PARTNER STUDIO — /partner  (PLAN_PARTNER.md, Fasa 1)
   ============================================================
   Ruang pembina module luar. Skrin:
     1. PIN (PartnerPinPintu)        2. Laman utama (fail ini)
     3. Page module = test lab (PartnerTestLab) — SATU page sahaja untuk setiap
        module (arahan user 27 Okt): master prompt, ujian, versi, hantar review,
        rename, padam. Page editor berasingan DIBUANG. New module · Open → terus ke sini.
     Admin urus Partner = AdminPartnerView (/admin/partners)

   Pintu (semua di SERVER, functions/partnerStudio.js):
     · akaun mesti dalam senarai Partner + status aktif
     · PIN Partner sendiri → token sesi 30 min, disemak pada SETIAP aksi
     · TIDAK bergantung pada lesen (keputusan user: Partner tak renew pun
       masih boleh sunting module dia)
   Bukan Partner → mesej ringkas; tiada menu dipaparkan kepada pelanggan.

   Draf master prompt yang belum disimpan dipegang di SINI (bukan dalam
   page module) — supaya tukar module dalam lab tak hilangkan draf, dan supaya ia tidak
   hilang bila sesi PIN tamat di tengah kerja.

   Draf AUTO-SAVE ke server (keputusan user 27 Okt): 1.5s selepas Partner
   berhenti menaip, draf dihantar (`drafSimpan`) — tak hilang bila tukar
   browser, dan admin nampak draf yang belum Save. Draf = versi tersimpan →
   draf server dibuang. Save version memadam draf di server.
   ============================================================ */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Box, Loader2, LogOut, RefreshCw } from 'lucide-react';
import '../design/tokens.css';
import { Btn, Badge, StatTile, PageHeader, EmptyState, Segmented } from '../design/ui';
import { panggilPartner, bacaToken, buangToken, sesiTamat, kodRalat, ralatRangkaian, masaLalu, statusModul, MODE_KUNCI, FORMAT_PILIHAN, labelFormat } from '../services/partnerStudioService';
import PartnerPinPintu from './PartnerPinPintu';
import PartnerTestLab from './PartnerTestLab';

/* ⛔ Tiada pemilih engine (keputusan user 2026-10-26, pilihan A) — engine diputuskan
   oleh SESAAT bila module dibuka kepada pelanggan (Fasa 3). Lihat partnerStudio.js. */
/* Format video (1 / 2 scene) dipilih DI SINI — keputusan user 29 Okt "Partner pilih". Dikunci selepas cipta. */
function ModulBaru({ onCipta, onBatal }) {
    const [nama, setNama] = useState('');
    const [format, setFormat] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');
    const hantar = async () => {
        if (nama.trim().length < 2) { setRalat('Enter a module name (at least 2 characters).'); return; }
        if (!format) { setRalat('Choose the video format.'); return; }
        setSibuk(true); setRalat('');
        try { await onCipta(nama.trim(), format); }
        catch (e) { setRalat(e?.message || 'Could not create the module.'); setSibuk(false); }
    };
    return (
        <div className="s-card" style={{ marginBottom: 14 }}>
            <div className="s-sect">
                <label style={{ maxWidth: 420 }}>
                    <span className="s-lbl">Module name</span>
                    <input className="s-input" value={nama} maxLength={60} autoFocus placeholder="Skincare Testimoni"
                        onChange={(e) => { setNama(e.target.value); setRalat(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') hantar(); }} />
                </label>
                <div>
                    <span className="s-lbl">Video format</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {FORMAT_PILIHAN.map((x) => (
                            <Btn key={x.n} size="sm" variant={format === x.n ? 'primary' : 'ghost'} onClick={() => { setFormat(x.n); setRalat(''); }}>{x.label}</Btn>
                        ))}
                        <span className="s-hint" style={{ margin: 0 }}>3–5 scenes: coming soon</span>
                    </div>
                    <p className="s-hint" style={{ margin: '6px 0 0' }}>Test results show as this card shape. You can change it until SESAAT approves the module.</p>
                </div>
                {ralat && <p className="s-hint" style={{ margin: 0, color: 'var(--s-stop)' }}>{ralat}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" onClick={hantar} disabled={sibuk}>{sibuk ? 'Creating…' : 'Create module'}</Btn>
                    <Btn size="sm" variant="ghost" onClick={onBatal} disabled={sibuk}>Cancel</Btn>
                </div>
            </div>
        </div>
    );
}

export default function PartnerStudioView() {
    const [fasa, setFasa] = useState('muat'); // muat | bukan | digantung | pin | studio | ralat
    const [status, setStatus] = useState(null);
    const [data, setData] = useState(null);   // hasil 'muat'
    const [ralat, setRalat] = useState('');
    const [skrin, setSkrin] = useState({ jenis: 'utama' }); // utama | lab
    const [drafs, setDrafs] = useState({});    // { [modId]: { prompt, asal, versiAsas } }
    const [modulBaru, setModulBaru] = useState(false);
    const [statusDraf, setStatusDraf] = useState({}); // { [modId]: { keadaan: 'menyimpan'|'disimpan'|'ralat', masa, ralat } }
    const pemasaDraf = useRef({});                      // pemasa debounce setiap module
    const terakhirDihantar = useRef({});                // JSON prompt terakhir yang server sudah terima
    const konflikDraf = useRef(new Set());               // modId yang drafnya diubah di tab/peranti lain (tunggu pilihan Partner)
    const sedangHantar = useRef({});                     // { [modId]: Promise<{ ok, jamMs }> } — auto-save yang masih berjalan (bug 5)
    const drafsRef = useRef(drafs);                      // draf TERKINI untuk hantaran semasa keluar page (bug 6)
    useEffect(() => { drafsRef.current = drafs; }, [drafs]);
    const [cubaSemula, setCubaSemula] = useState(0);    // naik → auto-save mencuba semula draf yang belum sampai
    const [tapis, setTapis] = useState('semua'); // semua | menunggu | perlu_ubah | lulus | draf
    const [menyegar, setMenyegar] = useState(false);
    const terakhirSegar = useRef(0);                     // masa (ms) senarai module terakhir dibaca dari server

    const keSkrinPin = useCallback(() => { buangToken(); setFasa('pin'); }, []);

    const muatStudio = useCallback(async () => {
        try {
            terakhirSegar.current = Date.now();
            const h = await panggilPartner('muat');
            setData(h);
            setFasa('studio');
        } catch (e) {
            if (sesiTamat(e)) { setFasa('pin'); return; }
            setRalat(e?.message || 'Could not load Partner Studio.');
            setFasa('ralat');
        }
    }, []);

    /* Bug 4 (1 Nov): status review (In review → Approved / Changes requested) dahulu dibaca SEKALI sahaja masa
       page dibuka — Partner yang biar tab terbuka tak nampak jawapan SESAAT sampai tekan F5, padahal portal
       ini satu-satunya saluran (tiada notifikasi, Keputusan J). Kini dibaca semula bila: kembali ke senarai,
       butang Refresh, dan tab browser dibuka semula (≥30s sejak bacaan terakhir). Draf dalam memori TIDAK
       disentuh — hanya senarai module, kuota dan Recent tests. */
    const segarStudio = useCallback(async ({ senyap = true } = {}) => {
        terakhirSegar.current = Date.now();
        try {
            setData(await panggilPartner('muat'));
            return true;
        } catch (e) {
            if (sesiTamat(e)) { keSkrinPin(); return false; }
            if (!senyap) setRalat(e?.message || 'Could not refresh.');
            return false;
        }
    }, [keSkrinPin]);
    const tekanSegar = async () => {
        setMenyegar(true); setRalat('');
        await segarStudio({ senyap: false });
        setMenyegar(false);
    };
    useEffect(() => {
        if (fasa !== 'studio') return undefined;
        const balik = () => {
            if (document.visibilityState === 'visible' && Date.now() - terakhirSegar.current > 30000) segarStudio();
        };
        document.addEventListener('visibilitychange', balik);
        window.addEventListener('focus', balik);
        return () => { document.removeEventListener('visibilitychange', balik); window.removeEventListener('focus', balik); };
    }, [fasa, segarStudio]);

    // Tiada setState SEBELUM await — fungsi ini dipanggil terus dari effect pertama.
    const semak = useCallback(async () => {
        try {
            const st = await panggilPartner('status');
            setStatus(st);
            if (!st.partner) { setFasa('bukan'); return; }
            if (st.status !== 'aktif') { setFasa('digantung'); return; }
            if (!st.pinAda || !bacaToken()) { setFasa('pin'); return; }
            await muatStudio();
        } catch (e) {
            setRalat(e?.message || 'Could not reach Partner Studio.');
            setFasa('ralat');
        }
    }, [muatStudio]);

    useEffect(() => {
        (async () => { await semak(); })();
    }, [semak]);
    const cubaLagi = () => { setFasa('muat'); setRalat(''); semak(); };

    /* Auto-save draf ke server — 1.5s selepas berhenti menaip. Gagal = BISING (status merah pada page).
       Bentuk draf: { prompt, asal, versiAsas, asasDraf (jamMs draf server yang tab ini lihat), drafBasi? }.
       ⭐ Pembaikan 27 Okt (bug #2/#4/#8):
         · sesi tamat → status jelas, dan draf DIHANTAR SEMULA sendiri selepas PIN (dahulu "Saving draft…"
           selama-lamanya dan tidak pernah sampai ke server);
         · ralat rangkaian → cuba semula sendiri setiap ~15s;
         · draf diubah di tab/peranti lain → server menolak ('aborted') → status 'konflik' + pilihan di page,
           TIDAK dicuba semula sendiri (dahulu tab terakhir menindih senyap);
         · draf yang bermula pada versi lama (drafBasi) kekal dihantar dengan versi asalnya. */
    useEffect(() => {
        if (fasa !== 'studio') return;
        for (const [modId, d] of Object.entries(drafs)) {
            const json = JSON.stringify(d.prompt);
            // hantarSemula: Save version memadam draf server — teks yang ditaip SEMASA simpan mesti dihantar lagi.
            if (terakhirDihantar.current[modId] === json && !d.paksa && !d.hantarSemula) continue;
            if (konflikDraf.current.has(modId) && !d.paksa) continue;
            clearTimeout(pemasaDraf.current[modId]);
            pemasaDraf.current[modId] = setTimeout(async () => {
                /* Bug 5 (1 Nov): SATU simpanan setiap module pada satu masa. Dahulu simpanan kedua bertolak sebelum yang
                   pertama pulang (server lambat > 1.5s, cth cold start) dengan nombor giliran LAMA → server menolak →
                   amaran palsu "changed in another tab" walaupun satu tab sahaja. Kini tunggu: bila simpanan semasa
                   pulang, `drafs` berubah (nombor giliran baharu) → effect ini menjadual semula dengan teks terkini. */
                if (sedangHantar.current[modId]) return;
                const sama = Object.values(MODE_KUNCI).every((k) => (d.prompt[k] || '') === (d.asal[k] || '')) && d.drafBasi == null;
                const asas = { modId, asasDraf: d.asasDraf ?? null, ...(d.paksa ? { paksa: true } : {}) };
                setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'menyimpan' } }));
                let pulang;
                sedangHantar.current[modId] = new Promise((r) => { pulang = r; });
                try {
                    const h = await panggilPartner('drafSimpan', sama ? { ...asas, buang: true } : { ...asas, prompt: d.prompt, versiAsas: d.drafBasi ?? d.versiAsas });
                    pulang({ ok: true, jamMs: h?.jamMs ?? null });
                    terakhirDihantar.current[modId] = json;
                    konflikDraf.current.delete(modId);
                    // Simpan nombor giliran baharu pada draf TERKINI (Partner mungkin sudah menaip lagi).
                    setDrafs((x) => (x[modId] ? { ...x, [modId]: { ...x[modId], asasDraf: h?.jamMs ?? null, paksa: false, hantarSemula: false } } : x));
                    setStatusDraf((x) => ({ ...x, [modId]: { keadaan: sama ? 'bersih' : 'disimpan', masa: Date.now() } }));
                } catch (e) {
                    pulang({ ok: false });
                    if (sesiTamat(e)) {
                        setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'ralat', ralat: 'your session ended. It will be saved after you enter your PIN.' } }));
                        buangToken(); setFasa('pin'); return;
                    }
                    if (kodRalat(e) === 'aborted') {
                        konflikDraf.current.add(modId);
                        setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'konflik', drafLain: e?.details?.drafLain || null } }));
                        return;
                    }
                    setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'ralat', ralat: ralatRangkaian(e)
                        ? "can't reach SESAAT right now (check your internet). Trying again shortly."
                        : `${e?.message || 'could not reach the server'} — trying again shortly.` } }));
                    setTimeout(() => setCubaSemula((n) => n + 1), 15000);
                } finally {
                    delete sedangHantar.current[modId];
                }
            }, 1500);
        }
    }, [drafs, fasa, cubaSemula]);
    /** Pilihan Partner bila draf diubah di tab/peranti lain: 'lain' = guna draf itu · 'saya' = kekalkan teks saya. */
    const selesaiKonflikDraf = async (modId, pilihan) => {
        const st = statusDraf[modId];
        if (pilihan === 'saya') { setDrafs((x) => ({ ...x, [modId]: { ...x[modId], paksa: true } })); return; }
        if (st?.drafLain) {
            const lain = st.drafLain;
            terakhirDihantar.current[modId] = JSON.stringify(lain.prompt);
            konflikDraf.current.delete(modId);
            setDrafs((x) => ({ ...x, [modId]: { ...x[modId], prompt: lain.prompt, asasDraf: lain.jamMs, drafBasi: lain.versiAsas !== x[modId].versiAsas ? lain.versiAsas : null, paksa: false } }));
            setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'disimpan', masa: Date.now() } }));
            return;
        }
        // Tiada draf lain (disimpan sebagai versi / dibuang di tab lain) → muat semula module dari server.
        try {
            const h = await panggilPartner('modulBaca', { modId });
            konflikDraf.current.delete(modId);
            tetapDraf(modId, drafDariServer(modId, h));
            kemaskiniModul(modId, { versiSemasa: h.versi });
            setStatusDraf((x) => ({ ...x, [modId]: null }));
        } catch (e) {
            if (sesiTamat(e)) { keSkrinPin(); return; }
            setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'konflik', drafLain: null, ralat: e?.message } }));
        }
    };
    /* Bug 6 (1 Nov): keluar page dalam app (klik menu sidebar) sebelum pemasa 1.5s berbunyi → dahulu pemasa
       DIBATALKAN dan teks itu hilang tanpa amaran (amaran tutup tab tidak berbunyi untuk navigasi dalam app).
       Kini draf yang belum sampai ke server DIHANTAR serta-merta semasa page ditutup — selepas simpanan yang
       masih berjalan pulang, dengan nombor gilirannya. Draf yang sedang konflik dibiarkan (Partner belum memilih).
       Gagal (cth sesi PIN tamat) = jerit dalam console; draf terakhir yang sudah sampai server kekal. */
    useEffect(() => () => {
        const pemasa = pemasaDraf.current;
        Object.values(pemasa).forEach(clearTimeout);
        for (const [modId, d] of Object.entries(drafsRef.current)) {
            if (!d || konflikDraf.current.has(modId)) continue;
            if (terakhirDihantar.current[modId] === JSON.stringify(d.prompt) && !d.paksa && !d.hantarSemula) continue;
            const sama = Object.values(MODE_KUNCI).every((k) => (d.prompt[k] || '') === (d.asal[k] || '')) && d.drafBasi == null;
            (sedangHantar.current[modId] || Promise.resolve({ ok: false }))
                .then((h) => panggilPartner('drafSimpan', {
                    modId, asasDraf: h.ok ? h.jamMs : (d.asasDraf ?? null), ...(d.paksa ? { paksa: true } : {}),
                    ...(sama ? { buang: true } : { prompt: d.prompt, versiAsas: d.drafBasi ?? d.versiAsas }),
                }))
                .catch((e) => console.error('[PartnerStudio] draf gagal dihantar semasa keluar page:', modId, e?.message));
        }
    }, []);

    // Amaran sebelum tutup tab — hanya bila draf BELUM sampai ke server (sedang menunggu / gagal).
    useEffect(() => {
        // ⚠️ statusDraf boleh mengandungi `null` (draf dikosongkan selepas "Reload from server") — WAJIB guard,
        //    kalau tidak `v.keadaan` melempar dan seluruh page Partner crash (bug pusingan 2 #6, 29 Okt).
        const adaDraf = Object.entries(drafs).some(([modId, d]) => terakhirDihantar.current[modId] !== JSON.stringify(d.prompt))
            || Object.values(statusDraf).some((v) => v && (v.keadaan === 'menyimpan' || v.keadaan === 'ralat'));
        if (!adaDraf) return undefined;
        const amaran = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', amaran);
        return () => window.removeEventListener('beforeunload', amaran);
    }, [drafs, statusDraf]);

    const kunciSesi = async () => {
        try { await panggilPartner('kunciSesi'); } catch { /* sesi tetap dibuang di browser */ }
        keSkrinPin();
    };

    const segarLarian = async () => {
        try {
            const { larian } = await panggilPartner('larianSenarai');
            setData((d) => (d ? { ...d, larian: larian.slice(0, 10) } : d));
        } catch (e) { if (sesiTamat(e)) keSkrinPin(); }
    };

    const ciptaModul = async (nama, format) => {
        try {
            const { modul } = await panggilPartner('modulCipta', { nama, format });
            setData((d) => ({ ...d, modules: [modul, ...d.modules] }));
            setModulBaru(false);
            await bukaLab(modul.id);
        } catch (e) {
            if (sesiTamat(e)) { keSkrinPin(); return; }
            throw e;
        }
    };

    const kemaskiniModul = (modId, ubah) => setData((d) => ({ ...d, modules: d.modules.map((m) => (m.id === modId ? { ...m, ...ubah } : m)) }));
    const tetapDraf = (modId, d) => setDrafs((x) => ({ ...x, [modId]: d }));
    /** Jawapan modulBaca → bentuk draf. Draf server (kalau ada) menang; asal = versi tersimpan terkini.
        Bug #8 (27 Okt): draf yang bermula pada versi LAMA ditanda `drafBasi` — Save version akan bertanya
        (bukan menindih versi yang lebih baru secara senyap). `asasDraf` = nombor giliran draf server. */
    const drafDariServer = (modId, h) => {
        const prompt = h.draf?.prompt || h.prompt;
        terakhirDihantar.current[modId] = JSON.stringify(prompt); // sudah ada di server — jangan hantar semula
        konflikDraf.current.delete(modId);
        if (h.draf) setStatusDraf((x) => ({ ...x, [modId]: { keadaan: 'disimpan', masa: h.draf.dikemaskini } }));
        const drafBasi = h.draf && Number(h.draf.versiAsas) !== Number(h.versi) ? Number(h.draf.versiAsas) || 0 : null;
        return { prompt, asal: h.prompt, versiAsas: h.versi, asasDraf: h.draf?.jamMs ?? null, drafBasi };
    };
    // Module dipadam di server → buang dari senarai + draf dalam memori, kembali ke laman utama.
    const lepasPadam = (modId) => {
        setData((d) => ({ ...d, modules: d.modules.filter((m) => m.id !== modId) }));
        setDrafs((x) => { const y = { ...x }; delete y[modId]; return y; });
        setSkrin({ jenis: 'utama' });
    };

    // Buka page module (test lab) → muat versi terkini (+ draf server) sebagai draf dahulu.
    async function bukaLab(modId) {
        setRalat('');
        if (!drafs[modId]) {
            try {
                const h = await panggilPartner('modulBaca', { modId });
                tetapDraf(modId, drafDariServer(modId, h));
                kemaskiniModul(modId, h.modul);   // bug 4: status review TERKINI dari server (dahulu dibuang)
            } catch (e) {
                if (sesiTamat(e)) { keSkrinPin(); return; }
                setRalat(e?.message || 'Could not open the module.');
                return;
            }
        }
        setSkrin({ jenis: 'lab', modId });
    }

    let isi;
    if (fasa === 'muat') {
        isi = <p className="s-hint" style={{ padding: 24 }}><Loader2 size={14} className="animate-spin" style={{ verticalAlign: -2 }} /> Loading Partner Studio…</p>;
    } else if (fasa === 'bukan') {
        isi = <EmptyState title="Partner Studio">This page is for SESAAT partners. If you've been invited, contact SESAAT to get access.</EmptyState>;
    } else if (fasa === 'digantung') {
        isi = <EmptyState title="Partner access suspended">Your partner access is currently suspended. Contact SESAAT for help. Modules you built are kept.</EmptyState>;
    } else if (fasa === 'ralat') {
        isi = <EmptyState title="Something went wrong" action={<Btn size="sm" onClick={cubaLagi}>Try again</Btn>}>{ralat}</EmptyState>;
    } else if (fasa === 'pin') {
        isi = <PartnerPinPintu pinAda={!!status?.pinAda} kunciSehingga={status?.kunciSehingga} onMasuk={() => { setStatus((s) => ({ ...s, pinAda: true })); muatStudio(); }} />;
    } else if (skrin.jenis === 'lab') {
        const modul = data.modules.find((m) => m.id === skrin.modId);
        if (!modul || !drafs[modul.id]) {
            isi = <EmptyState title="Module not found" action={<Btn size="sm" onClick={() => setSkrin({ jenis: 'utama' })}>Back</Btn>} />;
        } else {
            isi = (
                <PartnerTestLab
                    key={modul.id}
                    modul={modul}
                    draf={drafs[modul.id]}
                    onDraf={(d) => tetapDraf(modul.id, d)}
                    statusDraf={statusDraf[modul.id]}
                    onKonflikDraf={(pilihan) => selesaiKonflikDraf(modul.id, pilihan)}
                    hadPrompt={data.had.prompt}
                    onDisimpan={(ubah) => kemaskiniModul(modul.id, ubah)}
                    tetapanKreatif={data.tetapanKreatif}
                    kuota={data.kuota}
                    larian={data.larian}
                    onKuota={(k) => setData((d) => ({ ...d, kuota: k }))}
                    onLarian={segarLarian}
                    onSegar={() => segarStudio()}
                    onKembali={() => { setSkrin({ jenis: 'utama' }); segarStudio(); }}
                    onDipadam={() => lepasPadam(modul.id)}
                    onSesiTamat={keSkrinPin}
                />
            );
        }
    } else {
        const semuaStatus = data.modules.map((m) => ({ m, st: statusModul(m) }));
        const bil = (k) => semuaStatus.filter((x) => x.st.kunci === k).length;
        const dipapar = tapis === 'semua' ? semuaStatus : semuaStatus.filter((x) => x.st.kunci === tapis);
        isi = (
            <>
                <PageHeader
                    title="Partner Studio"
                    subtitle={`${status?.email || ''} · Partner active`}
                    suisBahasa={false}
                    actions={(
                        <>
                            <Btn size="sm" variant="ghost" icon={<RefreshCw size={14} className={menyegar ? 'animate-spin' : undefined} />} onClick={tekanSegar} disabled={menyegar}>Refresh</Btn>
                            <Btn size="sm" variant="ghost" icon={<LogOut size={14} />} onClick={kunciSesi}>Lock</Btn>
                            <Btn size="sm" icon={<Plus size={14} />} onClick={() => setModulBaru(true)} disabled={data.modules.length >= data.had.modul}>New module</Btn>
                        </>
                    )}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
                    <StatTile label="Lab tests today" value={`${data.kuota.guna} / ${data.kuota.had}`} />
                    <StatTile label="Your modules" value={`${data.modules.length} / ${data.had.modul}`} />
                    <StatTile label="In review" value={bil('menunggu')} />
                    <StatTile label="Approved" value={bil('lulus')} />
                </div>
                {ralat && <p className="s-hint" style={{ color: 'var(--s-stop)' }}>{ralat}</p>}
                {modulBaru && <ModulBaru onCipta={ciptaModul} onBatal={() => setModulBaru(false)} />}
                {data.modules.length === 0 && !modulBaru ? (
                    <EmptyState title="Build your first module" action={<Btn size="sm" icon={<Plus size={14} />} onClick={() => setModulBaru(true)}>New module</Btn>}>
                        Create a module, then paste and test your master prompt on the same page.
                    </EmptyState>
                ) : (
                    <>
                    <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                        <Segmented value={tapis} onChange={setTapis} options={[
                            { value: 'semua', label: `All ${data.modules.length}` },
                            { value: 'menunggu', label: `In review ${bil('menunggu')}` },
                            { value: 'perlu_ubah', label: `Changes requested ${bil('perlu_ubah')}` },
                            { value: 'lulus', label: `Approved ${bil('lulus')}` },
                            { value: 'draf', label: `Draft ${bil('draf')}` },
                        ]} />
                    </div>
                    {dipapar.length === 0 ? <p className="s-hint">No modules here.</p> : (
                    <div className="s-card">
                        <div className="s-list">
                            {dipapar.map(({ m, st }) => { return (
                                <div key={m.id} className="s-list__row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px' }}>
                                    <Box size={16} style={{ color: 'var(--s-ink-3)', flexShrink: 0 }} aria-hidden="true" />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="s-cardname">{m.nama}</div>
                                        <div className="s-dim" style={{ fontSize: 12 }}>
                                            {m.format ? `${labelFormat(m.format)} · ` : 'format not set · '}{m.versiSemasa ? `version ${m.versiSemasa} · saved ${masaLalu(m.dikemaskini)}` : 'not saved yet'}
                                            {drafs[m.id] && Object.values(MODE_KUNCI).some((k) => (drafs[m.id].prompt[k] || '') !== (drafs[m.id].asal[k] || '')) ? ' · unsaved changes' : ''}
                                        </div>
                                        {st.ket && <div className="s-dim" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.ket}</div>}
                                    </div>
                                    <Badge tone={st.tone}>{st.label}</Badge>
                                    <Btn size="sm" variant="ghost" onClick={() => bukaLab(m.id)}>Open</Btn>
                                </div>
                            ); })}
                        </div>
                    </div>
                    )}
                    </>
                )}
            </>
        );
    }

    return (
        <div className="s-root" style={{ minHeight: '100%', height: '100%', overflowY: 'auto', background: 'var(--s-ground)' }}>
            <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 16px 48px' }}>
                {isi}
            </div>
        </div>
    );
}
